import merge from 'deepmerge';
import { camelCase, chunk, map, values, take, partition, first, compact, flatMap, sortBy } from 'lodash';
import { Locale } from 'locale-enum';

import { BaseReport, StoreFormReport } from './store-form-report';
import { EquipmentName } from './equipment-name';
import { ItemStatusEnum } from '@pimm/services/lib/store-inspections';

namespace ICriticalWalkthroughReport {
  export type Item = Omit<BaseReport.Item, 'inputs'> & {
    dayparts: Record<number, Omit<BaseReport.Daypart, 'formGroups'> & { inputs: Record<string, BaseReport.Input[]> }>;
  };

  export type Section = Omit<BaseReport.Section, 'data'> & {
    data: Item[];
  };

  export type Form = Omit<BaseReport.Form, 'sections'> & {
    sections: Section[];
  };

  export type FormGroup = Omit<BaseReport.FormGroup, 'forms'> & {
    forms: Form[];
  };

  export type DaypartLeaders = Omit<BaseReport.Daypart, 'formGroups'>[];

  export type ReportDay = Omit<BaseReport.ReportDay, 'dayparts'> & {
    date: string;
    daypartLeaders: DaypartLeaders;
    formGroups: FormGroup[];
  };
}

export class CriticalWalkthroughReport extends StoreFormReport {
  public composeReport(locale: Locale): any {
    this.locale = locale;
    const planTables = ['correctiveActionsRootCause', 'managerOnDuty'];
    const reportDays = this.reportData?.reportDays ?? [];

    //#region Helpers
    /**
     * Merge two arrays of objects by a unique key.
     * If an object with the same key exists in both arrays,
     * it will be deeply merged using `deepmerge`.
     *
     * @param existing - The current array (accumulator).
     * @param incoming - The new array to merge in.
     * @param idKey - The property name that uniquely identifies an object.
     * @returns A new merged array of objects.
     */
    const mergeById = <T>(existing: T[], incoming: T[], idKey: keyof T): T[] => {
      const map = new Map<string, T>();

      // Store all existing entries in a Map for quick lookup by id
      existing.forEach((e: any) => map.set(e[idKey], e));

      incoming.forEach((i: any) => {
        const id = i[idKey];
        if (!id) return;

        if (map.has(id)) {
          // If already exists → deep merge incoming with existing
          map.set(id, merge(map.get(id)!, i, { arrayMerge }));
        } else {
          // If new → just insert
          map.set(id, i);
        }
      });

      return Array.from(map.values());
    };

    /**
     * Custom array merge strategy for deepmerge.
     * - If the array contains identifiable entities (by group/form/section/item id),
     *   merge them by ID instead of concatenating.
     * - If the array contains raw inputs, concatenate them.
     * - Otherwise, replace destination with source.
     */
    const arrayMerge = (dest: any[], source: any[], options: any): any[] => {
      if (dest.length && typeof dest[0] === 'object') {
        if ('packageDetailsGroupId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsGroupId');
        }
        if ('packageDetailsFormId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsFormId');
        }
        if ('packageDetailsSectionId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsSectionId');
        }
        if ('sectionLineItemId' in dest[0]) {
          return mergeById(dest, source, 'sectionLineItemId');
        }
        if ('dayparts' in dest[0]) {
          // For items with dayparts, merge the dayparts objects
          return dest.map((destItem: any) => {
            const sourceItem = source.find((s: any) => s.sectionLineItemId === destItem.sectionLineItemId);
            if (sourceItem) {
              return {
                ...destItem,
                dayparts: { ...destItem.dayparts, ...sourceItem.dayparts },
              };
            }
            return destItem;
          });
        }
        if ('inputs' in dest[0]) {
          // For input arrays, just concatenate (no merging by id)
          return dest.concat(source);
        }
      }
      // Default: replace with source
      return source;
    };

    /**
     * Flatten a form’s sections into a flat list of items
     * and attach daypart metadata to each item.
     *
     * @returns A ICriticalWalkthroughReport.Form with items and dayparts attached.
     */
    const mapItemsByForm = (_daypart: Partial<BaseReport.Daypart>, _form: BaseReport.Form) => {
      const sections = map(_form.sections, ({ data = [], ..._section }) => {
        const section: ICriticalWalkthroughReport.Section = {
          ..._section,
          data: data.map(({ inputs: _itemInputs, ..._item }) => {
            // Normalize inputs: pick the last recorded input for each key
            const inputs = Object.entries(_itemInputs).reduce((inputs: Record<string, BaseReport.Input[]>, [inputId, _inputs]) => {
              return { ...inputs, [inputId]: take(_inputs, 1) }; // get the last recorded input
            }, {});
            const item: ICriticalWalkthroughReport.Item = {
              dayparts: { [_daypart.partNumber!]: { ..._daypart, inputs } },
              ..._item,
            };
            return item;
          }),
        };
        return section;
      });

      const form: ICriticalWalkthroughReport.Form = {
        ..._form,
        sections: sections,
      };
      return form;
    };

    //#endregion

    /**
     * STEP 1
     * Transform all reportDays by:
     *  - Flattening sections → items
     *  - Merging formGroups/forms/items across dayparts by ID
     *  - Attaching daypart metadata to each item
     *
     * @param reportDays - The raw BaseReport.ReportDay[]
     * @returns Flattened and merged ICriticalWalkthroughReport.ReportDay[]
     */
    // prettier-ignore
    const normalizedReportByDates = reportDays.reduce((_reportDays: ICriticalWalkthroughReport.ReportDay[], { dayparts, ..._reportDay }) => {
      const reportDay: ICriticalWalkthroughReport.ReportDay = {
        ..._reportDay,
        daypartLeaders: [],
        formGroups: [],
      };

      for (const { formGroups = [], ..._daypart } of dayparts) {
        if (!formGroups.length) continue;

        // Merge all dayparts for this reportDay
        const nextFormGroups = formGroups.map(({ forms = [], ..._formGroup }) => {
          return { forms: forms.map(_form => mapItemsByForm(_daypart, _form)), ..._formGroup };
        });

        // Merge with accumulated formGroups using custom arrayMerge
        reportDay.formGroups = merge(reportDay.formGroups, nextFormGroups, { arrayMerge });
        // Append daypart leader
        reportDay.daypartLeaders.push(_daypart);
      }

      return [..._reportDays, reportDay];
    }, []);

    /**
     * Step 2
     * Transform combined data into composer readable body
     */
    const reportDay = normalizedReportByDates[0];

    // TODO: create composer type
    const compiledData: Record<string, any> = {
      reports: [],
    };

    if (reportDay) {
      const reportBody: Record<string, any> = {
        ...this.composerBody(),
        list: {},
        reviews: {},
        translations: {
          item: this.translate('item'),
          standard: this.translate('standard'),
          time: this.translate('time'),
          result: this.translate('result'),
          correctiveActions: this.translate('corrective_actions'),
          correctiveActionKeys: this.translate('corrective_action_keys'),
        },
      };

      // Page Leaders
      reportBody.reviews = {
        dayparts: reportDay.daypartLeaders.map(_ => {
          return this.composeReviewer({
            ..._.opsLeader,
            label: `${this.translate(_.label, _.translations)} - ${this.translate('ops_leader')}`,
          });
        }),
        generalManager: this.composeReviewer({
          ...reportDay.eodReview,
          useDefId: 'opsLeaderBorder',
          label: this.translate('general_manager'),
        }),
        districtManager: this.composeReviewer({
          ...reportDay.dmReview,
          label: this.translate('district_manager'),
        }),
      };

      // Not sure if we need to support multi forms in the future,
      // but for now always use the first form from the list
      const form = reportDay.formGroups[0]?.forms[0];
      const defs: any[] = [];

      // Page Body
      if (reportDay.daypartLeaders.length > 1) {
        // Multi daypart mode: use chunking pattern
        const countDayparts = reportDay.daypartLeaders.length;
        const noOfCols = countDayparts > 5 ? 3 : countDayparts;
        // Use actual partNumbers from daypartLeaders instead of range
        const partNumbers = reportDay.daypartLeaders.map(dp => dp.partNumber).sort((a, b) => (a ?? 0) - (b ?? 0));
        const groups = chunk(partNumbers, noOfCols);

        for (const _section of form.sections) {
          const items = _section.data.filter(_ => _.status !== ItemStatusEnum.Excluded);

          if (!items.length) continue;

          // TODO: need to verify
          // const itemWithMaxDayparts = maxBy(_section.data, item => keys(item.dayparts));
          // const maxDaypartsPerItem = itemWithMaxDayparts?.dayparts?.length ?? 0;
          const sectionKey = camelCase(_section.title);
          const isPlainTable = planTables.includes(sectionKey);

          // Append section header
          defs.push({
            useDefId: 'formHeader',
            title: this.translate(_section.title, _section.titleTranslations),
          });

          // groups = [[1,3], [4,5,6]] - only dayparts with formGroups
          for (const partNumbers of groups) {
            const prefixDefId = `${isPlainTable ? 'plain' : 'detailed'}${partNumbers.length}`;

            // Append item and daypart columns
            defs.push({
              useDefId: `${prefixDefId}DaypartHeader`,
              dayparts: partNumbers.map(_num => {
                const dp = reportDay.daypartLeaders.find(_ => _.partNumber === _num);
                return { value: dp ? this.translate(dp?.label, dp?.translations) : '' };
              }),
            });

            for (const { dayparts: itemDayparts, ..._item } of items) {
              const item = this.composeItem(_item);
              // Get input value per daypart
              /**
               * example data:
               * { 1: { ..., inputs: { 1: {} } }}
               * { 2: { ..., inputs: { 1: {} } }}
               */
              // Append item and its values
              defs.push({
                ...item,
                useDefId: `${prefixDefId}DaypartItem`,
                dayparts: partNumbers.map(_num => {
                  // prettier-ignore
                  const flattenInputs = values(itemDayparts[_num!]?.inputs).map(_inputs => first(_inputs)).filter(_input => !!_input) as BaseReport.Input[];
                  const input = this.composeInput(flattenInputs, _item.inputType);
                  return { ...input, inputSource: undefined };
                }),
              });
            }
          }
        }

        reportBody.list = {
          useDefId: 'multiDaypartList',
          sections: defs,
        };

        this.isLandscape = countDayparts > 3 && countDayparts < 6;
        reportBody.landscape = this.isLandscape;
      } else {
        // Single daypart mode: use chunking pattern
        for (const _section of form.sections) {
          const items = _section.data.filter(_ => _.status !== ItemStatusEnum.Excluded);

          if (!items.length) continue;

          // Group items by section
          // const sectionsByTitle = groupBy(items, item => item.section || 'Item');
          const sectionKey = camelCase(_section.title);
          const isPlainTable = planTables.includes(sectionKey);

          const section = {
            useDefTable: isPlainTable ? 'plain' : 'detailed',
            sectionKey: sectionKey,
            title: this.translate(_section.title, _section.titleTranslations),
            items: _section.data.map(_item => {
              let description = this.translate(_item.title, _item.titleTranslations);

              if (_item.isDynamicTitle) {
                const { name: equipmentName, suffix } = new EquipmentName(_item.title);
                if (equipmentName) {
                  description = [this.translate(`store_equipment:${equipmentName}`), suffix].filter(Boolean).join(' ');
                }
              }

              return {
                id: _item.sectionLineItemId,
                storeFormId: form.storeFormId,
                dayparts: values(_item.dayparts).map(daypart => {
                  // prettier-ignore
                  const flattenInputs = values(daypart.inputs).map(_inputs => first(_inputs)).filter(_input => !!_input) as BaseReport.Input[];
                  return this.composeInput(flattenInputs, _item.inputType);
                }),
                description: description,
                standard: this.translate(_item.standard, _item.standardTranslations),
              };
            }),
          };

          defs.push(section);
        }

        const [correctiveSections, sections] = partition(defs, _ => _.sectionKey === 'correctiveActionsRootCause');
        const chunkedSections = chunk(sections, Math.ceil(sections.length / 2));

        reportBody.list = {
          useDefId: 'singleDaypartList',
          sections: [...chunkedSections, correctiveSections],
        };
      }

      // Foot Notes
      const reportNotes = this.composeReportNotes(reportDays[0], undefined);
      let formNotes: any[] = [];

      for (const _daypart of reportDays[0]?.dayparts) {
        const forms = compact(flatMap(_daypart.formGroups, _ => _.forms)).filter(_ => !!_.notes?.length);
        const notes = sortBy(forms, _ => this.translate(_.title, _.titleTranslations)).flatMap(_form =>
          this.composeFormNotes(_form, _daypart),
        );
        formNotes = formNotes.concat(notes);
      }
      // append report notes
      if (reportNotes.length) {
        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('report_notes') });
        reportNotes.forEach(_def => reportBody.notes.push(_def));
      }
      // append form notes
      if (formNotes.length) {
        reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('form_notes') });
        formNotes.forEach(_def => reportBody.notes.push(_def));
      }

      compiledData.reports.push(reportBody);
    }

    return compiledData.reports[0];
  }
}
